import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, FileText } from 'lucide-react';
import { jobApplicationApi } from '@/lib/api/jobApplicationApi';
import { JobApplication } from '@/lib/models/jobApplication';
import { LoadingState, EmptyState } from '@/components/custom-ui/empty-state';
import { ConfirmDialog } from '@/components/custom-ui/ConfirmDialog';
import { NEXILINK_CARD_WHITE_ELEVATED, NEXILINK_SPACING } from '@/lib/nexilink-card-styles';
import ApplicationCard from './ApplicationCard';
import { useRehireConfirm } from './useRehireConfirm';

const ApplicationsPage: React.FC = () => {
    const { id: needId } = useParams<{ id: string }>();
    const navigate = useNavigate();
    const [applications, setApplications] = useState<JobApplication[]>([]);
    const [isLoading, setIsLoading] = useState(true);

    useEffect(() => {
        if (needId) loadApplications(needId);
    }, [needId]);

    const loadApplications = async (nid: string) => {
        setIsLoading(true);
        try {
            const result = await jobApplicationApi.getApplicationsForNeed(nid, 1, 50);
            setApplications(result.applications);
        } finally {
            setIsLoading(false);
        }
    };

    const { handleStatusUpdate, rehireDialog } = useRehireConfirm(async () => {
        if (needId) await loadApplications(needId);
    });

    if (isLoading) {
        return <LoadingState message="Laddar ansökningar..." />;
    }

    return (
        <div className={NEXILINK_SPACING}>
            <button
                onClick={() => navigate('/dashboard/needs')}
                className="flex items-center gap-2 text-gray-600 hover:text-gray-900 transition-colors"
            >
                <ArrowLeft className="w-4 h-4" />
                <span className="text-sm font-medium">Tillbaka till rekrytering</span>
            </button>

            <div>
                <h1 className="text-2xl font-bold text-gray-900">Ansökningar</h1>
                <p className="text-gray-500 mt-1">{applications.length} ansökningar</p>
            </div>

            {applications.length > 0 ? (
                <div className={NEXILINK_CARD_WHITE_ELEVATED}>
                    <div className="px-2 py-2 divide-y divide-gray-50">
                        {applications.map((app) => (
                            <ApplicationCard
                                key={app.id}
                                application={app}
                                onStatusUpdate={handleStatusUpdate}
                            />
                        ))}
                    </div>
                </div>
            ) : (
                <EmptyState
                    icon={FileText}
                    title="Inga ansökningar ännu"
                    description="När kandidater ansöker till denna tjänst visas de här."
                />
            )}

            <ConfirmDialog {...rehireDialog} />
        </div>
    );
};

export default ApplicationsPage;
